import { Injectable, OnDestroy, inject } from '@angular/core';
import { BehaviorSubject, Observable, Subscription } from 'rxjs';
import { AccountService, OrderSummaryPreviewResponse, Price, PriceConditions, PriceCount, Product, UserEditAddOnRequest, UserEditRequest, UserWithSeat } from 'src/api';
import { creditsAddonRequiresTopTierSeat, isCreditsAddonProduct } from '../utils/seat-product.util';
import { BillingInterval, DEFAULT_BILLING_INTERVAL } from '../common/billing-interval';
import { UserSeatService } from './user-seat.service';
import { environment } from 'src/environments/environment';

export interface PriceAndProduct {
  price: Price;
  product: Product;
}

@Injectable({
  providedIn: 'root',
})
export class SeatUtilsService implements OnDestroy {

  private readonly userSeatService = inject(UserSeatService);

  public products: Product[] = [];
  public prices: Price[] = [];

  public loaded$ = new BehaviorSubject<boolean>(false);

  private seeded = false;
  private initialLoadSub?: Subscription;

  constructor(private accountService: AccountService) {
    this.initialLoadSub = this.accountService.getPriceConditions().subscribe({
      next: (priceConditions) => this.tryApplyInitialPriceConditions(priceConditions),
      error: (error) => console.error('Error loading price conditions', error),
    });
  }

  ngOnDestroy(): void {
    this.initialLoadSub?.unsubscribe();
  }

  /** Authoritative update; cancels in-flight constructor fetch so it cannot overwrite later. */
  setPriceConditions(priceConditions: PriceConditions): void {
    this.seeded = true;
    this.initialLoadSub?.unsubscribe();
    this.initialLoadSub = undefined;
    this.products = priceConditions.products;
    this.prices = priceConditions.prices;
    this.loaded$.next(true);
  }

  private tryApplyInitialPriceConditions(priceConditions: PriceConditions): void {
    if (this.seeded) {
      return;
    }
    this.products = priceConditions.products;
    this.prices = priceConditions.prices;
    this.loaded$.next(true);
  }

  public getAllSeatTypes(): Product[] {
    return this.products.filter((product) => product.metadata?.['type'] === 'seat').sort((a, b) => (a.metadata?.['hierarchy_level'] ?? -1) - (b.metadata?.['hierarchy_level'] ?? -1));
  }

  public getAllAddOnTypes(): Product[] {
    return this.products.filter((product) => product.metadata?.['type'] === 'add_on');
  }

  getCurrentSeat(user: UserWithSeat): PriceAndProduct | null {
    return this.findPriceAndProductByPriceId(user.billing_price_id);
  }

  /**
   * Get add-ons from user's add_ons array
   */
  getCurrentAddOnPriceIds(user: UserWithSeat): { price_id: string; quantity: number }[] {
    if (!user.add_ons || !Array.isArray(user.add_ons)) return [];

    return user.add_ons
      .map((addon) => {
        const price_id = addon.add_on_price_id;
        const priceAndProduct = this.findPriceAndProductByPriceId(price_id);
        return priceAndProduct?.price.id || null;
      }).filter((addon) => addon !== null)
      .reduce((acc, price_id) => {
        const existing = acc.find((a) => a.price_id === price_id);
        if (existing) {
          existing.quantity++;
        } else {
          acc.push({ price_id, quantity: 1 });
        }
        return acc;
      }, [] as { price_id: string; quantity: number }[]);
  }


  /**
   * Whether an add-on can be booked for the given seat price.
   * Credits add-ons require the top-tier seat on Stripe (Premium). Deubner sells that upgrade on
   * every seat. The name check in isCreditsAddonProduct excludes Deubner content products, not
   * Deubner tenants. Other add-ons without dependsOnSeat are available for any selected seat.
   * dependsOnSeat may reference a seat price id or product id.
   */
  isAddonCompatibleWithSeat(addon: Product, seatPriceId: string | null | undefined): boolean {
    if (!seatPriceId) {
      return false;
    }

    if (isCreditsAddonProduct(addon) && creditsAddonRequiresTopTierSeat(environment.partner)) {
      return this.isTopTierSeat(seatPriceId);
    }

    const dependsOnSeat = addon.metadata?.['dependsOnSeat'];
    if (!dependsOnSeat) {
      return true;
    }

    if (dependsOnSeat === seatPriceId) {
      return true;
    }

    const seat = this.findPriceAndProductByPriceId(seatPriceId);
    if (!seat) {
      return false;
    }

    if (dependsOnSeat === seat.product.id) {
      return true;
    }

    const requiredSeat = this.findPriceAndProductByPriceId(dependsOnSeat);
    return requiredSeat?.product.id === seat.product.id;
  }

  private isTopTierSeat(seatPriceId: string): boolean {
    const seat = this.findPriceAndProductByPriceId(seatPriceId);
    if (!seat) {
      return false;
    }

    const seats = this.getAllSeatTypes();
    const definedLevels = seats
      .map((s) => s.metadata?.['hierarchy_level'])
      .filter((level): level is number => level !== undefined && level !== null);

    if (definedLevels.length > 0) {
      const maxLevel = Math.max(...definedLevels);
      const seatLevel = seat.product.metadata?.['hierarchy_level'];
      if (seatLevel !== undefined && seatLevel !== null) {
        return seatLevel === maxLevel;
      }
    }

    // Resolved per price, so this fallback ranks seats by the allowance they actually grant.
    const maxCredits = Math.max(...seats.map((s) => this.creditsForProduct(s)));
    const seatCredits = this.creditsForPrice(seat.price.id);
    return maxCredits > 0 && seatCredits === maxCredits;
  }

  /**
   * Check if a seat change is a downgrade
   */
  isDowngrade(currentProduct: Product | undefined, newProduct: Product | undefined): boolean {
    if (!currentProduct) return false;
    if (!newProduct) return true;
    const currentLevel = currentProduct.metadata?.['hierarchy_level'] ?? 0;
    const newLevel = newProduct.metadata?.['hierarchy_level'] ?? 0;
    return currentLevel > newLevel;
  }

  /**
   * Check if a seat change is an upgrade
   */
  isUpgrade(currentProduct: Product | undefined, newProduct: Product | undefined): boolean {
    if (!currentProduct) return false;
    if (!newProduct) return true;
    const currentLevel = currentProduct.metadata?.['hierarchy_level'] ?? -1;
    const newLevel = newProduct.metadata?.['hierarchy_level'] ?? -1;
    return currentLevel < newLevel;
  }

  calculatePriceDiff(previousPrices: PriceCount[], newPrices: PriceCount[]): Map<string, number> {
    const diffMap = new Map<string, number>();
    for (const price of previousPrices) {
      diffMap.set(price.price_id, -price.count);
    }
    for (const price of newPrices) {
      const priceId = price.price_id;
      const previousCount = diffMap.get(priceId) ?? 0;
      diffMap.set(priceId, previousCount + price.count);
    }
    return diffMap;
  }

  /**
   * Check if there are any product changes
   */
  hasProductChanges(productChanges: OrderSummaryPreviewResponse): boolean {
    const previousProducts = productChanges.previous_prices;
    const newProducts = productChanges.new_prices;
    const diff = this.calculatePriceDiff(previousProducts, newProducts);
    return Array.from(diff.values()).some((count) => count !== 0);
  }

  /**
   * Check if there are any downgrades (i.e. seats being downgraded or add-ons being removed)
   */
  hasDowngrades(userChanges: UserEditRequest[], initialPendingChanges: Map<string, { role: UserWithSeat.RoleEnum; seatPriceId: string | undefined; addOns: UserEditAddOnRequest[] }>): boolean {
    for (const userChange of userChanges) {
      // Check if seat was downgraded
      const initialChangeState = initialPendingChanges.get(userChange.user_id);
      if (!initialChangeState) continue;
      const currentUserSeat = initialChangeState.seatPriceId ? this.findPriceAndProductByPriceId(initialChangeState.seatPriceId) : undefined;
      const desiredUserSeat = this.findPriceAndProductByPriceId(userChange.seat_price_id);
      if (this.isDowngrade(currentUserSeat?.product, desiredUserSeat?.product)) {
        return true;
      }
      // Handle add-ons
      const currentUserAddOns = initialChangeState.addOns;
      const desiredUserAddOns = userChange.add_ons;
      for (const addOn of desiredUserAddOns) {
        const addOnProduct = this.findPriceAndProductByPriceId(addOn.price_id);
        if (!addOnProduct) continue;
        const currentAddonQuantity = currentUserAddOns.find((a) => a.price_id === addOnProduct.price.id)?.quantity ?? 0;
        const desiredAddonQuantity = addOn.quantity;
        if (currentAddonQuantity > desiredAddonQuantity) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Check if there are any upgrades (i.e. seats being upgraded or add-ons being added)
   */
  hasUpgrades(userChanges: UserEditRequest[], initialPendingChanges: Map<string, { role: UserWithSeat.RoleEnum; seatPriceId: string | undefined; addOns: UserEditAddOnRequest[] }>): boolean {
    for (const userChange of userChanges) {
      // Check if seat was downgraded
      const initialChangeState = initialPendingChanges.get(userChange.user_id);
      if (!initialChangeState) continue;
      const currentUserSeat = initialChangeState.seatPriceId ? this.findPriceAndProductByPriceId(initialChangeState.seatPriceId) : undefined;
      const desiredUserSeat = this.findPriceAndProductByPriceId(userChange.seat_price_id);
      if (this.isUpgrade(currentUserSeat?.product, desiredUserSeat?.product)) {
        return true;
      }
      // Handle add-ons
      const currentUserAddOns = initialChangeState.addOns;
      const desiredUserAddOns = userChange.add_ons;
      for (const addOn of desiredUserAddOns) {
        const addOnProduct = this.findPriceAndProductByPriceId(addOn.price_id);
        if (!addOnProduct) continue;
        const currentAddonQuantity = currentUserAddOns.find((a) => a.price_id === addOnProduct.price.id)?.quantity ?? 0;
        const desiredAddonQuantity = addOn.quantity;
        if (currentAddonQuantity < desiredAddonQuantity) {
          return true;
        }
      }
    }
    return false;
  }

  /**
   * Get seat price by type
   */
  getSeatPrice(priceId: string): number {
    return this.findPriceAndProductByPriceId(priceId)?.price.unit_amount ?? 0;
  }

  /**
   * The credits allowance one unit of `priceId` grants.
   *
   * The price's own `credits` metadata wins when it names one, and the product's is the default —
   * mirroring `PricingService.get_credits_for_price` on the backend, which is what actually sets
   * `max_credits`. Reading only the product here would show a figure the backend does not enforce.
   */
  public creditsForPrice(priceId: string | undefined | null): number {
    const found = this.findPriceAndProductByPriceId(priceId);
    if (!found) return 0;
    return statedCredits(found.price.metadata) ?? statedCredits(found.product.metadata) ?? 0;
  }

  /**
   * The credits `product` grants at the price it would be booked on right now.
   *
   * The booking price rather than the default one, for the same reason `bookingPriceId` exists: it is
   * the price the firm would actually be charged, and on a yearly plan that is not the default.
   */
  public creditsForProduct(product: Product | undefined | null): number {
    return this.creditsForPrice(this.bookingPriceId(product));
  }

  public formatPrice(price_in_cents: number): string {
    return (price_in_cents / 100).toLocaleString('de-DE', { style: 'currency', currency: 'EUR' });
  }

  public getOrderPreview(userEditRequests: UserEditRequest[]): Observable<OrderSummaryPreviewResponse> {
    return this.accountService.generateOrderSummaryPreview({
      userEditRequest: userEditRequests
    })
  }

  public findPriceAndProductByPriceId(priceId: string | undefined | null): PriceAndProduct | null {
    if (!priceId) return null;
    const price = this.prices.find((price) => price.id === priceId);
    if (!price) return null;
    const product = this.products.find((product) => product.price_ids.includes(price.id));
    if (!product) return null;
    return { price, product };
  }

  public findProductByProductId(productId: string | undefined | null): Product | undefined {
    if (!productId) return undefined;
    return this.products.find((product) => product.id === productId) ?? undefined;
  }

  /**
   * The interval the firm is billed on, read from a seat it already holds.
   *
   * Derived rather than stored: Stripe requires every item of one subscription to share an interval,
   * so any booked seat answers for the whole firm — and this works on screens that cannot load the
   * admin-only billing context. Monthly is the default because that is how every subscription starts.
   */
  public currentInterval(users?: { billing_price_id?: string | null }[] | null): BillingInterval {
    const candidates = users?.length ? users : [this.userSeatService.user()];
    for (const user of candidates) {
      const interval = this.findPriceAndProductByPriceId(user?.billing_price_id)?.price.interval;
      if (interval === 'month' || interval === 'year') return interval;
    }
    return DEFAULT_BILLING_INTERVAL;
  }

  /**
   * A product's price for `interval`, falling back to its default price.
   *
   * Booking a monthly price onto a yearly subscription is rejected by Stripe, so every place that used
   * to reach for `default_price_id` has to come through here.
   */
  public priceForInterval(product: Product | undefined | null, interval: BillingInterval): Price | undefined {
    if (!product) return undefined;
    const byId = (id: string | undefined | null) => this.prices.find((price) => price.id === id);
    // The default wins when it already recurs at `interval`, and inactive prices are skipped: the same
    // order as the backend's `get_price_for_interval`. Taking whichever `price_ids` listed first meant
    // a product carrying a second active price at that interval got booked at the one the product is
    // no longer sold at — and at a price the backend would have resolved differently.
    const preferred = byId(product.default_price_id);
    if (preferred?.active && preferred.interval === interval) return preferred;
    const match = (product.price_ids ?? [])
      .map(byId)
      .find((price) => price?.active && price.interval === interval);
    // Still the default when nothing recurs at `interval`, so a catalogue without that interval sells.
    return match ?? preferred;
  }

  /** The price id to book `product` at for `interval`. */
  public priceIdForInterval(product: Product | undefined | null, interval: BillingInterval): string | undefined {
    return this.priceForInterval(product, interval)?.id;
  }

  /**
   * The price id to book `product` at right now.
   *
   * Everywhere a seat or add-on is booked goes through here rather than reaching for
   * `default_price_id`: a monthly price added to a yearly subscription is rejected by Stripe.
   */
  public bookingPriceId(product: Product | undefined | null): string | undefined {
    return this.priceIdForInterval(product, this.currentInterval());
  }

  /** The price `product` is booked at right now, for showing what it costs. */
  public bookingPrice(product: Product | undefined | null): Price | undefined {
    return this.priceForInterval(product, this.currentInterval());
  }
}


/**
 * The `credits` figure this metadata states, or null when it states none.
 *
 * Absent, blank and unparseable all mean "says nothing", so the next source down is consulted. A
 * stated zero is a real answer and stops the search: a price may grant no credits at all.
 */
function statedCredits(metadata: Record<string, unknown> | null | undefined): number | null {
  const raw = metadata?.['credits'];
  if (raw === undefined || raw === null || (typeof raw === 'string' && !raw.trim())) return null;
  const value = Number(raw);
  return Number.isFinite(value) ? value : null;
}
